import { ComponentProps, useId, useRef, useState } from "react";
import { isWeb, Label, styled, Text, View, ViewProps } from "tamagui";

import { Button } from "./button";

const getLableMaxWidth = (totalWidth: number, percent: number) => {
  return Math.round((totalWidth / 100) * percent);
};

const FileInputLabelText = styled(Text, {
  name: "FileInputLabelText",
  fontFamily: "$body",
  fontWeight: "$5",
  fontSize: "$4",
  lineHeight: "$4",
  color: "$gray-primary-900",
  numberOfLines: 1,
  ellipsizeMode: "tail",
});

const ErrorMessage = styled(Text, {
  name: "ErrorMessage",
  ellipsizeMode: "tail",
  numberOfLines: 1,
  position: "absolute",
  left: "$2",
  backgroundColor: "white",
  paddingHorizontal: 2,
  color: "$error-500",
  fontFamily: "$subtitle",
  fontSize: "$1",
  lineHeight: "$1",
  fontWeight: "$4",
  bottom: -6,
});

export const FileInput: React.FC<
  ViewProps & {
    mineTypes?: string[];
    onChange?: React.ChangeEventHandler<HTMLInputElement> | undefined;
    label?: string;
    error?: string;
  }
> = ({ mineTypes, children, error, label, onChange, ...rest }) => {
  const id = useId();
  const [width, setWidth] = useState(0);

  const fileInputId = "file-input-" + id;

  if (!isWeb) return null;

  return (
    <View
      data-id={"file-input-container"}
      borderColor={"$gray-secondary-400"}
      borderWidth={"$0.2"}
      borderStyle="dashed"
      borderRadius={"$5"}
      minHeight={123}
      width={"100%"}
      position="relative"
      onLayout={(e) => setWidth(e.nativeEvent.layout.width)}
      {...rest}
    >
      <Label
        position="absolute"
        top={-14}
        left={"$2"}
        padding={0}
        backgroundColor={"white"}
        htmlFor={fileInputId}
        data-id="file-input-label"
      >
        <FileInputLabelText maxWidth={getLableMaxWidth(width, 90)}>
          {label}
        </FileInputLabelText>
      </Label>
      <input
        data-id="file-input"
        id={fileInputId}
        type="file"
        accept={mineTypes?.join(",")}
        onChange={onChange}
        style={{
          cursor: "pointer",
          opacity: 0,
          position: "absolute",
          top: 0,
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
          pointerEvents: "all",
        }}
      />
      {children}

      {error && (
        <ErrorMessage maxWidth={getLableMaxWidth(width, 90)}>
          {error}
        </ErrorMessage>
      )}
    </View>
  );
};

type ButtonProps = ComponentProps<typeof Button>;

export const FilePicker: React.FC<
  ButtonProps & {
    mineTypes?: string[];
    onChange?: React.ChangeEventHandler<HTMLInputElement> | undefined;
  }
> = ({ children, onChange, mineTypes, ...rest }) => {
  const ref = useRef<HTMLInputElement | null>(null);
  return (
    <>
      <Button
        onPress={() => {
          if (ref.current) {
            ref.current.click();
          }
        }}
        {...rest}
      >
        {children}
      </Button>
      <input
        ref={ref}
        type="file"
        accept={mineTypes?.join(",")}
        style={{
          display: "none",
        }}
        onChange={onChange}
      />
    </>
  );
};
